import { Skeleton } from '@/components/ui/app-skeleton';
import { Panel, PanelContent, PanelHeader } from '@/components/ui/panel';
import { cn } from '@/lib/ui/utils';

export interface PortfolioLoadingSkeletonProps {
  /**
   * PnL card column: `'own'` includes Deposit / Withdraw / gift placeholders.
   * `'other'` matches public-profile cards (tiles only). Defaults to `'other'`
   * so dispatcher + other-profile gates stay CLS-safe until viewer is known.
   */
  pnlViewerMode?: 'own' | 'other';
}

/**
 * Page-level placeholders for `/portfolio` and `/portfolio/[user_id]` while auth
 * or public-user data resolves. Mirrors {@link PortfolioBody} spacing and the
 * spec 081 + 082 top row (`ProfilePortfolioCard` + `PortfolioPnlCard`).
 */

function PnlStatTileSkeletonPlaceholders(): React.ReactNode {
  return (
    <div className="flex flex-1 flex-col gap-0.5 rounded-panel-inset border border-border-secondary bg-background-primary px-3 py-2.5 sm:px-4 sm:py-3">
      <Skeleton className="h-3 w-12" />
      <Skeleton className="mt-0.5 h-6 w-14 max-w-full" />
    </div>
  );
}

function BackerRewardStatSkeleton(): React.ReactNode {
  return (
    <div className="flex flex-col gap-1.5 rounded-panel-inset border border-border-secondary bg-background-primary px-3 py-2.5 sm:px-4 sm:py-3">
      <Skeleton className="h-3 w-16" />
      <Skeleton className="h-5 w-20 max-w-full" />
    </div>
  );
}

function ProfilePortfolioCardSkeleton(): React.ReactNode {
  return (
    <Panel className="relative h-full min-w-0 gap-0 pt-0">
      <div className="relative" aria-hidden="true">
        <Skeleton className="aspect-[3/1] w-full rounded-t-card" />
        <div className="absolute -bottom-12 left-4 sm:left-6">
          <Skeleton className="size-24 shrink-0 rounded-full ring-4 ring-background-primary" />
        </div>
      </div>
      <div className="flex justify-end px-4 pt-2 sm:px-6">
        <Skeleton className="size-10 shrink-0 rounded-control" />
      </div>
      <div
        className={cn(
          'flex min-w-0 flex-col gap-0.5 px-4 pb-0 pt-5 sm:px-6', // ARBITRARY: mirrors live ProfilePortfolioCard avatar-overlap spacing
        )}
      >
        <Skeleton className="h-7 w-40 max-w-[85%]" />
        <Skeleton className="h-4 w-28 max-w-[70%]" />
      </div>
      <div className="mt-2 flex items-center gap-x-4 px-4 pb-0 sm:px-6">
        <Skeleton className="h-5 w-[5.5rem]" />
        <Skeleton className="h-5 w-[5.75rem]" />
      </div>
    </Panel>
  );
}

function PortfolioPnlCardSkeleton({
  viewerMode,
}: {
  viewerMode: 'own' | 'other';
}): React.ReactNode {
  return (
    <Panel className="h-full min-w-0">
      <PanelContent className="flex-1">
        <div
          className="flex min-h-60 flex-col justify-between gap-4 md:min-h-70"
          data-testid="portfolio-pnl-card-loading"
          data-viewer={viewerMode}
        >
          <div className="flex items-start gap-4">
            <div className="flex min-w-0 flex-1 basis-0 flex-col gap-1">
              <Skeleton className="h-3 w-12" />
              <Skeleton className="h-12 max-w-[min(100%,12rem)] w-48" />
              <Skeleton className="h-4 max-w-[min(100%,10rem)] w-40" />
            </div>
            <Skeleton className="h-20 min-h-20 min-w-0 flex-1 basis-0 rounded-card" />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <PnlStatTileSkeletonPlaceholders />
            <PnlStatTileSkeletonPlaceholders />
            <PnlStatTileSkeletonPlaceholders />
          </div>
          {viewerMode === 'own' ? (
            <div
              className="flex flex-wrap gap-2 sm:flex-nowrap"
              data-testid="portfolio-pnl-card-loading-cta"
            >
              <Skeleton className="h-12 min-w-0 flex-1 sm:min-w-28" />
              <Skeleton className="h-12 min-w-0 flex-1 sm:min-w-28" />
              <Skeleton className="h-12 min-w-0 flex-1 sm:min-w-28" />
            </div>
          ) : null}
        </div>
      </PanelContent>
    </Panel>
  );
}

function PositionsPanelSkeleton(): React.ReactNode {
  return (
    <Panel className="min-w-0">
      <PanelContent className="flex flex-col gap-4">
        <div className="flex items-center justify-between gap-2">
          <Skeleton className="h-7 w-40" />
          <Skeleton className="h-9 w-[11rem]" />
        </div>
        <div
          className="flex flex-col gap-2"
          data-testid="positions-loading-skeletons"
          aria-busy="true"
        >
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </div>
      </PanelContent>
    </Panel>
  );
}

export function PortfolioLoadingSkeleton({
  pnlViewerMode = 'other',
}: PortfolioLoadingSkeletonProps): React.ReactNode {
  return (
    <main className="w-full space-y-4">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-5">
        <div className="min-w-0 md:col-span-2 md:h-full">
          <ProfilePortfolioCardSkeleton />
        </div>
        <div className="min-w-0 md:col-span-3 md:h-full">
          <PortfolioPnlCardSkeleton viewerMode={pnlViewerMode} />
        </div>
      </div>

      <PositionsPanelSkeleton />

      {/* Backer rewards — full-width panel: 3-stat header (earned / claimable /
          markets backed) over a backed-markets list. */}
      <Panel className="min-w-0">
        <PanelHeader className="pb-0">
          <Skeleton className="h-7 w-40" />
        </PanelHeader>
        <PanelContent className="flex flex-col gap-4 pt-4 pb-0">
          <div className="grid grid-cols-3 gap-3">
            <BackerRewardStatSkeleton />
            <BackerRewardStatSkeleton />
            <BackerRewardStatSkeleton />
          </div>
          <Skeleton className="h-40 w-full rounded-card" />
        </PanelContent>
      </Panel>

      {/* Theses — full-width panel (stacks below backer rewards, not beside it). */}
      <Panel className="min-w-0">
        <PanelHeader className="pb-0">
          <Skeleton className="h-7 w-32" />
        </PanelHeader>
        <PanelContent className="pt-4 pb-0">
          <Skeleton className="h-28 w-full rounded-card" />
        </PanelContent>
      </Panel>

      {/* Recent activity — full-width panel at the bottom. */}
      <Panel className="min-w-0">
        <PanelHeader className="pb-0">
          <Skeleton className="h-7 w-44" />
        </PanelHeader>
        <PanelContent className="flex flex-col gap-2 pt-4 pb-0">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
        </PanelContent>
      </Panel>
    </main>
  );
}
